import { combineReducers, configureStore } from '@reduxjs/toolkit';

import devTools from '@paragon/dev-tools/index';
import { defaultServerUrls, environment, getEnvironmentOverride } from '@paragon/environment-tools';
import { changeTracker, insertions, layoutModels } from '@paragon/layout-models';
import { logInData } from '@paragon/login-tools';
import { materialLists } from '@paragon/material-lists';
import { notifications } from '@paragon/notification-tools';
import { spinnerMessages } from '@paragon/task-spinner';
import { theme } from '@paragon/themes';

import { initializeSyncClient } from '../syncClientManager';
import { displaySynchronizationError } from './actions/synchronizationActions';
import connectStoreHandler from './handlers';
import { activeAnalysisSetMiddleware } from './middlewares/activeAnalysisSetMiddleware';
import { componentDesignSynchronizationMiddleware } from './middlewares/componentDesignSynchronizationMiddleware';
import { deprecationWatchdogMiddleware } from './middlewares/deprecationWatchdogMiddleware';
import { editorMiddleware } from './middlewares/editorMiddleware';
import { incompletePlaneCutMiddleware } from './middlewares/incompletePlaneCutMiddleware';
import { interactiveEditManagementMiddleware } from './middlewares/interactiveEditManagementMiddleware';
import { invalidCarriedComponentConnectionMiddleware } from './middlewares/invalidCarriedComponentConnectionMiddleware';
import { invalidGeometryMiddleware } from './middlewares/invalidGeometryMiddleware';
import { invalidRelativeMiddleware } from './middlewares/invalidRelativeGeometryMiddleware';
import { saveComponentDesignMiddleware } from './middlewares/saveComponentDesignMiddleware';
import { stalePickedPointMovementMiddleware } from './middlewares/stalePickedPointMovementMiddleware';
import { stationSynchronizationMiddleware } from './middlewares/stationSynchronizationMiddleware';
import { syncWatchdogMiddleware } from './middlewares/syncWatchdogMiddleware';
import { analysisHistory } from './slices/analysisHistorySlice';
import { analysisResultRequests } from './slices/analysisResultRequestsSlice';
import { analysisSets } from './slices/analysisSetsSlice';
import { application } from './slices/applicationSlice';
import { asyncRequests } from './slices/asyncRequestsSlice';
import { cameraSlice } from './slices/camera/cameraSlice';
import { canaryDownload } from './slices/canaryDownloadSlice';
import { componentDesigns } from './slices/componentDesignsSlice';
import { componentDesignTimestamps } from './slices/componentDesignTimestampsSlice';
import { delayedNotifications } from './slices/delayedNotificationsSlice';
import { dimensions } from './slices/dimensionsSlice';
import { editor } from './slices/editorSlice';
import { hangerConfigurations } from './slices/hangerConfigurationsSlice';
import { interactiveEditing } from './slices/interactiveEditingSlice';
import { inventoryPrices } from './slices/inventoryPricesSlice';
import { jackGirderConnections } from './slices/jackGirderConnectionsSlice';
import { layout } from './slices/layoutSlice';
import { livePlating } from './slices/livePlatingSlice';
import { loadSets } from './slices/loadSetsSlice';
import { localUserDimensions } from './slices/localUserDimensionsSlice';
import { longRunningOperations } from './slices/longRunningOperationsSlice';
import { lumberPropertySets } from './slices/lumberPropertySetsSlice';
import { optimizationCostComparison } from './slices/optimizationCostComparisonSlice';
import { optimizationProgress } from './slices/optimizationProgressSlice';
import { optimizationSettings } from './slices/optimizationSettingsSlice';
import { panelData } from './slices/panelDataSlice';
import { plateProperties } from './slices/platePropertiesSlice';
import { popup } from './slices/popupSlice';
import { productionModels } from './slices/productionModelsSlice';
import { save } from './slices/saveSlice';
import { selections } from './slices/selectionsSlice';
import { settingsDatabase } from './slices/settingsDatabaseSlice';
import { sharedComponentSet } from './slices/sharedComponentSetSlice';
import { snappingUnderlaySlice } from './slices/snappingUnderlaySlice';
import { stations } from './slices/stationsSlice';
import { trussGroups } from './slices/trussGroupsSlice';
import { usageData } from './slices/usageDataSlice';
import { userSettings } from './slices/userSettingsSlice';
import { viewport } from './slices/viewportSlice';
import { visualOptions } from './slices/visualOptionsSlice';

const rootReducer = combineReducers({
  analysisHistory,
  analysisResultRequests,
  analysisSets,
  application,
  asyncRequests,
  camera: cameraSlice.reducer,
  canaryDownload,
  changeTracker,
  componentDesignTimestamps,
  componentDesigns,
  delayedNotifications,
  dimensions,
  editor,
  environment,
  hangerConfigurations,
  insertions,
  interactiveEditing,
  inventoryPrices,
  jackGirderConnections,
  layout,
  layoutModels,
  livePlating,
  loadSets,
  logInData,
  longRunningOperations,
  lumberPropertySets,
  materialLists,
  notifications,
  optimizationCostComparison,
  optimizationProgress,
  optimizationSettings,
  panelData,
  plateProperties,
  popup,
  productionModels,
  save,
  selections,
  settingsDatabase,
  sharedComponentSet,
  snappingUnderlay: snappingUnderlaySlice.reducer,
  spinnerMessages,
  stations,
  theme,
  trussGroups,
  localUserDimensions,
  usageData,
  userSettings,
  viewport,
  visualOptions,
});

const currentEnvironment = getEnvironmentOverride() ?? process.env.APP_ENV!;
export const syncClient = initializeSyncClient(defaultServerUrls[currentEnvironment].synchronizationServerUrl);

const devToolsEnhancer = devTools.loadForRedux();

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .prepend(stationSynchronizationMiddleware, componentDesignSynchronizationMiddleware)
      .concat(
        saveComponentDesignMiddleware,
        invalidGeometryMiddleware,
        syncWatchdogMiddleware,
        deprecationWatchdogMiddleware,
        syncClient.state.middleware,
        editorMiddleware,
        invalidCarriedComponentConnectionMiddleware,
        invalidRelativeMiddleware,
        activeAnalysisSetMiddleware,
        interactiveEditManagementMiddleware,
        incompletePlaneCutMiddleware,
        stalePickedPointMovementMiddleware,
      ),
  devTools: false,
  enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(devToolsEnhancer ? [devToolsEnhancer] : []),
});

connectStoreHandler(store);

syncClient.state.store = store;
syncClient.on('error', () => {
  store.dispatch(displaySynchronizationError());
});

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
